// ─────────────────────────────────────────────────────────────────────────────
// LabThree — Guida "Creare una ricetta"
// Centro assistenza → guida per amministratori, solo piano TRACE.
//
// È la guida più lunga della configurazione: la ricetta è il punto in cui
// ingredienti, prodotti e fasi si tengono insieme.
//
// Schermate RICOSTRUITE (vedi la nota in atomi.js). Le etichette fra virgolette
// sono copiate dal codice.
//
// Riferimenti nel codice, per chi dovrà aggiornarla:
//   Schermata ......... App.js, GestioneRicette — tre sezioni: "Dati generali",
//                       "Fasi e ingredienti", "Dati tecnici & Nutrizionali"
//   Peso/pz ........... campo form.peso_per_pezzo_g, compare SOLO se
//                       unita_misura === 'pz'
//   Riscalatura ....... scala = lavorazione.quantita_prevista /
//                       ricetta.quantita_prevista. Usata nel riquadro
//                       "Disponibilità ingredienti" del dettaglio lavorazione e
//                       nel bilancio di massa.
//   Ingr. "generale" .. tr_ricette_ingredienti.id_ricetta_fase NULL = non legato
//                       a una fase specifica
//   Semilavorati ...... tr_ricette_ingredienti.id_semilavorato (tipo
//                       'SEMILAVORATO')
//   CAVEAT ARCHITETTURALE (docs/ANALISI_TRACCIABILITA.md, P1-7 e P1-8):
//     le lavorazioni CHIUSE leggono la ricetta viva, non uno snapshot: modificare
//     una ricetta cambia quello che si legge nelle lavorazioni passate. E il
//     salvataggio è delete-all + reinsert non transazionale. Da qui il consiglio
//     del capitolo 7. Se un giorno si introduce il versioning, quel capitolo va
//     riscritto.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import LT from '../../ui/tokens';
import I, { Ico } from '../../ui/icons';
import {
  GuidaLayout, H, P, Strong, Code, Tasto, Callout, FAQ,
} from './atomi';

const SEZIONI = [
  { id: 'r0', label: 'Prima di iniziare' },
  { id: 'r1', label: '1. I dati generali' },
  { id: 'r2', label: '2. La quantità è la scala' },
  { id: 'r3', label: '3. Quante fasi servono' },
  { id: 'r4', label: '4. Gli ingredienti' },
  { id: 'r5', label: '5. I semilavorati' },
  { id: 'r6', label: '6. Dati tecnici' },
  { id: 'r7', label: '7. Modificarla dopo' },
  { id: 'r8', label: 'Le trappole' },
  { id: 'r9', label: 'Domande frequenti' },
];

// ─────── Il confronto fra zero, una e più fasi ───────────────────────────────

const CASI = [
  {
    titolo: 'Nessuna fase',
    quando: 'Sconsigliato.',
    ok: ['Apri e chiudi la lavorazione', 'Nasce il lotto di prodotto finito'],
    no: ['Non sai dove si trova il prodotto', 'Niente giacenze per ubicazione', 'Alla chiusura non puoi dire dove resta'],
    bg: LT.n100, fg: LT.n600, bd: LT.n200,
  },
  {
    titolo: 'Una fase',
    quando: 'Il caso normale quando la produzione è un blocco unico, o il dettaglio non vi serve.',
    ok: ['Sai dove sta il prodotto', 'Giacenze per ubicazione', 'Chiusura con le ubicazioni', 'Un solo passaggio da aprire e chiudere'],
    no: ['Non distingui i momenti della produzione'],
    bg: LT.verdeSoft, fg: '#166534', bd: '#C7EFDA',
  },
  {
    titolo: 'Più fasi',
    quando: 'Quando il prodotto si ferma in posti diversi o in momenti diversi, o c\'è qualcosa da misurare lungo la strada.',
    ok: ['Sai in quale reparto è adesso', 'Parametri rilevati per fase', 'Cali e scarti separati per passaggio'],
    no: ['Ogni fase va aperta e chiusa: più gesti per l\'operatore'],
    bg: LT.bluSoft, fg: LT.blu, bd: '#D8E6FE',
  },
];

const Confronto = () => (
  <div style={{
    display: 'flex', flexWrap: 'wrap', gap: 10, margin: '22px 0',
  }}>
    {CASI.map(c => (
      <div key={c.titolo} style={{
        flex: '1 1 210px', minWidth: 210,
        background: LT.n0, border: `1px solid ${c.bd}`, borderRadius: LT.r.lg,
        overflow: 'hidden',
      }}>
        <div style={{
          background: c.bg, color: c.fg, padding: '9px 12px',
          fontSize: 13.5, fontWeight: 800,
        }}>{c.titolo}</div>
        <div style={{ padding: '11px 12px' }}>
          <div style={{ fontSize: 12.5, color: LT.n600, lineHeight: 1.5, marginBottom: 10 }}>
            {c.quando}
          </div>
          {c.ok.map(t => (
            <div key={t} style={{
              display: 'flex', gap: 7, alignItems: 'flex-start',
              fontSize: 12.5, color: LT.n700, marginBottom: 4, lineHeight: 1.4,
            }}>
              <Ico d={I.check} size={13} stroke={LT.verde} style={{ marginTop: 2, flexShrink: 0 }} />
              <span>{t}</span>
            </div>
          ))}
          {c.no.map(t => (
            <div key={t} style={{
              display: 'flex', gap: 7, alignItems: 'flex-start',
              fontSize: 12.5, color: LT.n500, marginBottom: 4, lineHeight: 1.4,
            }}>
              <Ico d={I.x} size={13} stroke={LT.n400} style={{ marginTop: 2, flexShrink: 0 }} />
              <span>{t}</span>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

export default function CreareRicetta() {
  return (
    <GuidaLayout
      titolo="Creare una ricetta"
      sottotitolo="È il punto in cui ingredienti, prodotti e fasi si tengono insieme. Un pomeriggio speso bene qui rende automatico tutto quello che viene dopo, per anni."
      durata="~ 18 minuti"
      destinatario="Per amministratori · piano TRACE"
      sezioni={SEZIONI}
    >

      {/* r0 */}
      <H id="r0">Prima di iniziare</H>

      <P>
        La ricetta descrive <Strong>come si fa un prodotto</Strong>: con quali ingredienti, in che
        quantità, attraverso quali passaggi. È lo stampo da cui nasce ogni lavorazione: quando
        aprirete una produzione, il programma leggerà la ricetta e vi proporrà da solo cosa serve
        e in che dose.
      </P>

      <Callout kind="info" titolo="Cosa deve esistere prima">
        Il <Strong>prodotto</Strong> che volete ottenere, gli <Strong>ingredienti</Strong> che ci
        entrano, e — se decidete di usarle — le <Strong>fasi produttive</Strong>. Se una tendina è
        vuota, manca uno di questi tre. Le fasi si possono anche aggiungere per prime dalla loro
        anagrafica e richiamare qui.
      </Callout>

      <P>
        La schermata ha tre sezioni: <Strong>Dati generali</Strong>,{' '}
        <Strong>Fasi e ingredienti</Strong>, e <Strong>Dati tecnici &amp; Nutrizionali</Strong>. Si
        salva tutto insieme con <Tasto>Salva ricetta</Tasto>.
      </P>

      {/* r1 */}
      <H id="r1" cat="produzione">1. I dati generali</H>

      <P>
        Due campi obbligatori: il <Strong>Prodotto collegato</Strong>, cioè cosa esce da questa
        ricetta, e il <Strong>Nome ricetta</Strong>. Il nome serve perché lo stesso prodotto può
        averne più di una — il campo suggerisce "Es. Ricetta base, Variante estiva…" e rende bene
        l'idea.
      </P>
      <P>
        Poi c'è la <Strong>Quantità prevista</Strong> con la sua <Strong>Unità</Strong>, che merita
        il capitolo suo qui sotto. Se scegliete <Code>pz</Code> come unità compare un campo in più,{' '}
        <Strong>Peso/pz (g)</Strong>: è il peso medio di un pezzo in grammi, e serve al programma
        per mettere in relazione i pezzi con i chili quando i due mondi si incontrano.
      </P>

      {/* r2 */}
      <H id="r2">2. La quantità prevista è la scala</H>

      <P>
        Questo è il meccanismo che vale la pena capire bene, perché è quello che vi risparmia di
        riscrivere le dosi ogni volta.
      </P>
      <P>
        La quantità prevista della ricetta <Strong>non è quanto produrrete</Strong>. È la{' '}
        <Strong>base di riferimento</Strong> a cui si riferiscono le dosi che scrivete. Quando poi
        aprite una lavorazione, indicate quanto volete produrre <em>quella volta</em>, e il
        programma riscala tutti gli ingredienti in proporzione.
      </P>

      <div style={{
        margin: '20px 0', padding: 16, background: LT.n50,
        border: `1px solid ${LT.n150}`, borderRadius: LT.r.lg,
      }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: LT.navy, marginBottom: 9 }}>
          Un esempio
        </div>
        <div style={{ fontSize: 13.5, color: LT.n700, lineHeight: 1.7 }}>
          Ricetta scritta su <Strong>100 kg</Strong>: 80 kg di carne, 2 kg di sale, 0,4 kg di spezie.<br />
          Lavorazione aperta per <Strong>250 kg</Strong> → il programma propone{' '}
          <Strong>200 kg di carne, 5 kg di sale, 1 kg di spezie</Strong>.<br />
          <span style={{ color: LT.n500, fontSize: 12.5 }}>
            Nel dettaglio della lavorazione lo vedete scritto: <Code>× 2,500</Code>.
          </span>
        </div>
      </div>

      <P>
        La conseguenza pratica è che conviene scrivere la ricetta su una base{' '}
        <Strong>comoda da leggere</Strong>, non sulla produzione tipica. Cento chili, cento litri,
        mille pezzi: con una base tonda le dosi si controllano a occhio e gli errori si vedono.
      </P>

      <Callout kind="warn" titolo="Se la base è vuota, non si riscala niente">
        Senza quantità prevista il programma non ha un rapporto da applicare e usa le dosi così
        come sono, qualunque sia la dimensione della lavorazione. Le proposte diventano sbagliate
        appena producete una quantità diversa. È un campo formalmente facoltativo che nella pratica
        va sempre compilato.
      </Callout>

      {/* r3 */}
      <H id="r3">3. Quante fasi servono</H>

      <P>
        Domanda giusta, e la risposta non è "il più possibile". Le fasi servono a sapere{' '}
        <Strong>dov'è il prodotto</Strong> e a <Strong>misurare qualcosa</Strong> lungo la strada.
        Se non vi servono nessuna delle due cose, non aggiungetele.
      </P>

      <Confronto />

      <P>
        Il criterio che funziona è questo: <Strong>si aggiunge una fase quando il prodotto si
        ferma</Strong> — in un posto diverso, o in un momento diverso — <Strong>oppure quando c'è
        un valore da rilevare</Strong>. Non per descrivere ogni gesto.
      </P>
      <P>
        Impastare e insaccare nella stessa mezz'ora sullo stesso banco: una fase. Insaccare e poi
        stagionare quaranta giorni in cantina, dove va controllata la temperatura: due fasi, e la
        seconda vi dice che quel lotto è in cantina e non in sala lavorazione.
      </P>

      <Callout kind="success" titolo="Una fase sola è una scelta legittima">
        Non è un ripiego né una configurazione incompleta. Se la vostra produzione è un blocco
        unico, una fase sola — chiamatela "Produzione", o come la chiamate voi — vi dà tutto quello
        che conta: aprire, dichiarare, sapere dove sta il prodotto, chiudere. Il livello di
        dettaglio lo decidete voi, non il programma.
      </Callout>

      <P>
        Le fasi si aggiungono con <Tasto>Aggiungi fase</Tasto> e si mettono nell'ordine in cui
        avvengono. Per ognuna si possono indicare una <Strong>durata</Strong> indicativa in minuti e
        delle note, e — se la fase ne prevede dalla sua anagrafica — dei parametri da rilevare, che
        l'operatore compilerà chiudendo quella fase.
      </P>

      {/* r4 */}
      <H id="r4" cat="temperature">4. Gli ingredienti</H>

      <P>
        Con <Tasto>Aggiungi ingrediente</Tasto> si costruisce la composizione. Per ogni riga si
        scelgono l'ingrediente, la quantità e l'unità. Ma c'è una scelta in più che conviene
        capire: <Strong>a quale fase appartiene</Strong>.
      </P>
      <P>
        Gli ingredienti si possono legare a una fase precisa — il sale va nell'impasto, non nella
        stagionatura — oppure lasciare nel gruppo degli{' '}
        <Strong>ingredienti non legati a una fase specifica</Strong>, che nelle schermate compaiono
        marcati come <Code>(generale)</Code>.
      </P>
      <P>
        Serve a due cose concrete. La prima è che l'operatore, aprendo una fase, si vede proposti{' '}
        <em>solo</em> gli ingredienti di quella fase, invece di tutta la lista. La seconda è che il
        legame fra ingrediente e fase rende più leggibile la tracciabilità: si sa in che momento
        della produzione un lotto è entrato.
      </P>

      <Callout kind="tip" titolo="Se hai una fase sola, non pensarci">
        Con una fase sola la distinzione non serve: metti tutto lì, o lascia tutto generale, è
        indifferente. La scelta per fase paga quando le fasi sono davvero più di una.
      </Callout>

      {/* r5 */}
      <H id="r5">5. I semilavorati</H>

      <P>
        In una ricetta si può mettere, al posto di un ingrediente comprato, un{' '}
        <Strong>semilavorato</Strong> prodotto da voi. È così che si costruisce la catena a due
        livelli: la ricetta del salame non elenca carne e spezie, elenca l'impasto — e l'impasto ha
        una ricetta sua.
      </P>
      <P>
        Quando poi aprirete la lavorazione del salame, il programma vi chiederà{' '}
        <Strong>quale lotto di impasto</Strong> state usando, esattamente come vi chiede quale
        lotto di carne. Ed è quel gesto che, il giorno di un richiamo, permette di risalire dal
        salame all'impasto e dall'impasto ai lotti del fornitore.
      </P>

      <Callout kind="info" titolo="Prima la ricetta del semilavorato">
        Per usare l'impasto nella ricetta del salame, l'impasto deve esistere come prodotto di
        categoria semilavorato e avere una ricetta sua. L'ordine è: prima la ricetta di quello che
        sta a monte, poi quella di quello che sta a valle.
      </Callout>

      {/* r6 */}
      <H id="r6" cat="formazione">6. Dati tecnici e nutrizionali</H>

      <P>
        La terza sezione ha un sottotitolo che spiega da sé perché vale la pena compilare bene le
        anagrafiche: <Strong>"Valori nutrizionali calcolati e lista ingredienti per
        etichetta"</Strong>.
      </P>
      <P>
        <Strong>Calcolati.</Strong> Non li scrivete voi: nascono dai valori che avete messo sugli
        ingredienti, pesati sulle quantità della ricetta. È qui che ripaga la ricerca automatica
        della guida sugli ingredienti — quei numeri presi dalla banca dati o dal codice a barre
        arrivano fin qui da soli.
      </P>
      <P>
        E la <Strong>lista ingredienti per etichetta</Strong> è l'elenco in ordine di quantità
        decrescente, che è il modo in cui va scritto per legge. Da questa sezione nascono la scheda
        tecnica del prodotto e il cartello con il QR code.
      </P>

      {/* r7 */}
      <H id="r7">7. Modificarla dopo</H>

      <P>
        Una ricetta si modifica in qualsiasi momento. Ma c'è una cosa da sapere, ed è il motivo per
        cui questo capitolo esiste: <Strong>le lavorazioni già chiuse leggono la ricetta di
        oggi</Strong>, non una copia di com'era il giorno in cui hanno prodotto.
      </P>
      <P>
        In pratica: se cambiate le dosi di una ricetta, andando a guardare una produzione dell'anno
        scorso vedrete la composizione nuova, non quella vera di allora. Per una correzione di un
        refuso non cambia niente. Per una modifica sostanziale, cambia molto.
      </P>

      <Callout kind="warn" titolo="Le due regole che vi tengono al sicuro">
        <Strong>Non modificate una ricetta mentre c'è una lavorazione aperta</Strong> su quel
        prodotto: state cambiando il terreno sotto i piedi a chi sta lavorando.<br /><br />
        <Strong>Se la ricetta cambia davvero, createne una nuova</Strong> invece di riscrivere
        quella vecchia. Il campo Nome esiste anche per questo: "Ricetta base" e "Ricetta base
        rev.2" convivono, le lavorazioni vecchie restano legate alla loro, e la storia resta
        vera. È lo stesso criterio delle revisioni dei documenti.
      </Callout>

      {/* r8 */}
      <H id="r8">Le trappole</H>

      <div style={{ margin: '16px 0' }}>
        {[
          ['La quantità prevista non è quanto produci',
            'È la base su cui sono scritte le dosi. Scrivila tonda — 100 kg, 1000 pz — e lascia che sia la lavorazione a dire quanto produci davvero.'],
          ['Senza quantità prevista non c\'è riscalatura',
            'Le dosi vengono proposte così come sono, qualunque sia la dimensione della lavorazione. Compilala sempre.'],
          ['Zero fasi toglie le giacenze',
            'I movimenti di ubicazione sono agganciati a una fase: senza nemmeno una fase non si sa dove sta il prodotto e alla chiusura non si può dichiararlo. Il minimo utile è una fase.'],
          ['Modificare una ricetta cambia anche il passato',
            'Le lavorazioni chiuse leggono la ricetta viva. Per una modifica sostanziale conviene una ricetta nuova.'],
          ['Il semilavorato va creato prima',
            'Per usarlo in una ricetta deve già esistere come prodotto semilavorato con la sua ricetta. Si costruisce da monte a valle.'],
        ].map(([t, d]) => (
          <div key={t} style={{
            display: 'flex', gap: 11, alignItems: 'flex-start',
            padding: '11px 0', borderBottom: `1px solid ${LT.n150}`,
          }}>
            <Ico d={I.alert} size={16} stroke={LT.arancio} style={{ marginTop: 3, flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: LT.n900 }}>{t}</div>
              <div style={{ fontSize: 13.5, color: LT.n600, lineHeight: 1.55, marginTop: 2 }}>{d}</div>
            </div>
          </div>
        ))}
      </div>

      {/* r9 */}
      <H id="r9">Domande frequenti</H>

      <FAQ q="Devo essere preciso al grammo nelle dosi?">
        No, e il programma è costruito per non pretenderlo. Le quantità della ricetta sono una
        previsione: in lavorazione l'operatore dichiara quello che ha usato davvero, e le
        differenze sono tollerate senza blocchi. Metti le dosi che usi normalmente e vai avanti.
      </FAQ>

      <FAQ q="Lo stesso prodotto può avere due ricette?">
        Sì, ed è previsto: si distinguono col nome. Aprendo una lavorazione scegli quale usare.
        Serve per le varianti stagionali, per due formati, o per tenere una revisione nuova
        accanto a quella vecchia.
      </FAQ>

      <FAQ q="Posso scrivere la ricetta senza aver ancora inserito tutti gli ingredienti?">
        Puoi salvarla incompleta e aggiungere righe dopo. Ricorda solo che quello che manca nella
        ricetta non verrà proposto in lavorazione, e che i valori nutrizionali calcolati saranno
        parziali fino a quando la composizione non è completa.
      </FAQ>

      <FAQ q="A cosa serve la durata della fase?">
        È indicativa, per chi legge la ricetta. Non fa partire nessun contatore e non blocca
        niente: la durata vera di una fase è quella fra la sua apertura e la sua chiusura, che
        l'operatore registra lavorando.
      </FAQ>

      <FAQ q="Se cancello una riga di ingrediente perdo qualcosa delle lavorazioni passate?">
        Le lavorazioni conservano i lotti che hanno consumato, e quelli non si toccano. Quello che
        cambia è la composizione teorica che vedi sulla ricetta, e quindi il confronto del bilancio
        di massa per le produzioni passate. Un altro motivo per preferire una ricetta nuova quando
        i cambiamenti sono sostanziali.
      </FAQ>

      <div style={{
        marginTop: 40, padding: 18, background: LT.bluSoft,
        borderRadius: LT.r.lg, border: `1px solid ${LT.n150}`,
      }}>
        <div style={{ fontSize: 15.5, fontWeight: 700, color: LT.navy, marginBottom: 4 }}>
          E adesso?
        </div>
        <div style={{ fontSize: 14, color: LT.n700, lineHeight: 1.65 }}>
          Scrivi la ricetta di <em>un</em> prodotto, con una base tonda e le fasi che ti servono
          davvero — anche una sola. Con quella in mano la configurazione è finita: il passo
          successivo è far entrare della merce e aprire la prima lavorazione, e da lì il programma
          comincia a lavorare per te.
        </div>
      </div>

    </GuidaLayout>
  );
}
